import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  HTMLSelect,
  NumericInput,
  Tab,
  Tabs,
} from '@blueprintjs/core'

import { WeldingParams } from '../components/WeldingParams'
import type { Store } from '../store'

/**
 * Preferences modal — default WPS (copied into every new operation),
 * typography, theme.
 *
 * Mirrors the SolidWorks "System Options → Default Process" convention:
 * editing a value here never retroactively changes existing operations.
 * Each operation holds its own snapshot (see store.Operation.wps).
 */
export function PreferencesDialog({
  isOpen,
  onClose,
  store,
}: {
  isOpen: boolean
  onClose: () => void
  store: Store
}) {
  const [draft, setDraft] = useState(store.preferences)
  const [tab, setTab] = useState<'process' | 'typography' | 'theme'>('process')

  const commit = () => {
    store.setPreferences(draft)
    onClose()
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Preferences"
      icon="cog"
      style={{ width: 640 }}
    >
      <DialogBody>
        <Tabs
          id="prefs-tabs"
          vertical
          selectedTabId={tab}
          onChange={(id) => setTab(id as 'process' | 'typography' | 'theme')}
        >
          <Tab
            id="process"
            title="Default process"
            panel={
              <>
                <p className="tree-dim" style={{ marginTop: 0 }}>
                  Copied into every new operation. Existing operations keep
                  their own snapshot — change those from the Property Manager.
                </p>
                <WeldingParams
                  wps={draft.defaultWps}
                  onChange={(wps) => setDraft({ ...draft, defaultWps: wps })}
                />
              </>
            }
          />
          <Tab
            id="typography"
            title="Typography"
            panel={
              <>
                <FormGroup label="Base font size (px)">
                  <NumericInput
                    min={10}
                    max={20}
                    value={draft.fontSize}
                    onValueChange={(v) =>
                      setDraft({ ...draft, fontSize: Math.round(v) || 13 })
                    }
                  />
                </FormGroup>
              </>
            }
          />
          <Tab
            id="theme"
            title="Theme"
            panel={
              <FormGroup label="UI theme">
                <HTMLSelect
                  value={draft.theme}
                  onChange={(e) =>
                    setDraft({ ...draft, theme: e.target.value as 'dark' | 'light' })
                  }
                  options={[
                    { label: 'Dark', value: 'dark' },
                    { label: 'Light', value: 'light' },
                  ]}
                />
              </FormGroup>
            }
          />
        </Tabs>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button intent="primary" onClick={commit}>
              Apply
            </Button>
          </>
        }
      />
    </Dialog>
  )
}
